import { useEffect, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import ViewingMode from 'shared/components/viewing-mode-controller/types/viewing-mode';
import Permission from 'shared/features/auth/authorization/enums/permission';
import usePermissionAuthorization from 'shared/features/auth/authorization/hooks/permission-authorization';

import BulkScenarioActiveState from 'features/residents/models/bulk-scenario-active-state';
import { BulkUpdateOrganisationUnitScenarioIsActiveCommand } from 'features/residents/models/bulk-update-organisation-unit-scenario-is-active';
import Resident from 'features/residents/models/resident';
import { useLazyReadBulkAvailableScenarios } from 'features/residents/resident-settings/providers/resident-queries';

export type ResidentSettingsBulkEditState = {
  selectResidentSettings: (newSelectedResidentSettings: Resident[]) => void;
  clearResidentSettings: () => void;
  residentSettingBulkEditForm: UseFormReturn<BulkUpdateOrganisationUnitScenarioIsActiveCommand>;
  selectedResidentSettings: Resident[];
  viewingMode: ViewingMode;
  availableScenariosIsLoading: boolean;
};

const useResidentSettingsBulkEditState = (): ResidentSettingsBulkEditState => {
  const { hasPermission } = usePermissionAuthorization();

  const residentSettingBulkEditForm = useForm<BulkUpdateOrganisationUnitScenarioIsActiveCommand>({
    mode: 'onBlur',
    defaultValues: {
      bulkScenarioActiveStates: [],
      organisationUnitIds: [],
    },
  });

  const [viewingMode, setViewingMode] = useState<ViewingMode>('none');
  const [selectedResidentSettings, setSelectedResidentSettings] = useState<Resident[]>([]);

  const {
    refetch: getBulkAvailableScenarios,
    data: availableScenarios,
    isPending: availableScenariosIsLoading,
  } = useLazyReadBulkAvailableScenarios({ organisationUnitIds: selectedResidentSettings.map((resident) => resident.organisationUnitId) });

  const selectResidentSettings = (newSelectedResidentSettings: Resident[]): void => {
    setSelectedResidentSettings(newSelectedResidentSettings);
  };

  const clearResidentSettings = (): void => {
    setSelectedResidentSettings([]);
  };

  useEffect(() => {
    if (selectedResidentSettings.length > 0 && hasPermission(Permission.UpdateResidentsSettings)) {
      setViewingMode('editing');
      getBulkAvailableScenarios();
    } else {
      setViewingMode('none');
    }
  }, [selectedResidentSettings, getBulkAvailableScenarios]);

  useEffect(() => {
    residentSettingBulkEditForm.reset({
      bulkScenarioActiveStates: (availableScenarios?.availableScenarios ?? []).map(
        (scenarioResponse) =>
          ({
            scenarioType: scenarioResponse.scenarioType,
            isActive: scenarioResponse.isAnyActive,
          }) as BulkScenarioActiveState,
      ),
      organisationUnitIds: selectedResidentSettings.map((resident) => resident.organisationUnitId),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableScenarios, selectedResidentSettings]);

  return {
    selectResidentSettings,
    clearResidentSettings,
    residentSettingBulkEditForm,
    selectedResidentSettings,
    viewingMode,
    availableScenariosIsLoading,
  };
};

export default useResidentSettingsBulkEditState;
